import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { ARTIFACT_TYPES, CopilotContext, COPILOT_FUNCTION_NAMES } from '@Sage/ssm-web5';
import { DASHBOARD } from 'constants/Entities';
import { DASHBOARD_TYPE } from 'constants/DashboardProviders';
import { COPILOT_DOMAINS } from 'utils/copilotRegistry/constants';
import { logEvent } from 'utils/tracking';
import { DashboardContext } from './DashboardContext';
import DailyDashboard from './dashboards/DailyDashboard';
import SFMDashboard from './dashboards/SFMDashboard';
import GenericExternalDashboard from './dashboards/GenericExternalDashboard';
import CustomBackground from './components/CustomBackground';
import DashboardForceBI from '../DashboardForceBI';

const DashboardContainer = memo(() => {
    const [loadedDashboards, setLoadedDashboards] = useState([]);
    const { activeDashboard, dashboards, setActiveDashboard, forceBIRef } =
        useContext(DashboardContext);
    const { getMethodsForCopilot } = useContext(CopilotContext);

    const copilotDashboardId = activeDashboard?.id ? String(activeDashboard.id) : null;

    const handleDashboardArtifact = useCallback(
        (artifact) => {
            const targetId = artifact?.props?.id;
            if (!targetId) return;

            const targetIdStr = String(targetId);
            const targetDashboard = dashboards?.find((d) => String(d.id) === targetIdStr);

            if (targetIdStr === copilotDashboardId) {
                // Currently viewing this dashboard — refresh it
                forceBIRef?.current?.refreshDashboard?.();
            } else if (targetDashboard) {
                // Switch to the target dashboard
                setActiveDashboard(targetDashboard);
            }
        },
        [copilotDashboardId, dashboards, forceBIRef, setActiveDashboard],
    );

    useEffect(() => {
        getMethodsForCopilot.registerFeatureMethods(COPILOT_DOMAINS.SSM, {
            getCurrentDashboardId: () => copilotDashboardId,
            isForceBIDashboard: () => activeDashboard?.type?.value === DASHBOARD_TYPE.FORCE_BI,
            refreshDashboard: () => forceBIRef?.current?.refreshDashboard?.(),
        });
        getMethodsForCopilot.registerFunctions({
            [COPILOT_FUNCTION_NAMES.GET_CURRENT_DASHBOARD]: ({ show } = {}) => {
                // Keep compatibility with backend contract while "show" is intentionally unused.
                void show;
                return { id: copilotDashboardId };
            },
        });
        getMethodsForCopilot.registerArtifactHandlers({
            [ARTIFACT_TYPES.DASHBOARD]: handleDashboardArtifact,
        });
    }, [
        activeDashboard?.type?.value,
        copilotDashboardId,
        forceBIRef,
        getMethodsForCopilot,
        handleDashboardArtifact,
    ]);

    useEffect(() => {
        if (!activeDashboard) return;
        if (loadedDashboards.find((d) => d.id === activeDashboard.id)) return;
        setLoadedDashboards([...loadedDashboards, activeDashboard]);
    }, [activeDashboard, loadedDashboards]);

    const dashboardsLoaded = useMemo(() => {
        const getDashboardComponent = (dashboard) => {
            const typeValue = dashboard?.type?.value;
            switch (typeValue) {
                case DASHBOARD_TYPE.POWER_BI:
                case DASHBOARD_TYPE.METABASE:
                    if (typeValue === DASHBOARD_TYPE.POWER_BI) {
                        logEvent({
                            event: DASHBOARD.trueName,
                            submodule: 'powerBI',
                            functionality: 'detailView',
                        });
                    }
                    return <GenericExternalDashboard id={dashboard.id} />;
                case DASHBOARD_TYPE.SFM:
                    return <SFMDashboard id={dashboard.id} />;
                case DASHBOARD_TYPE.FORCE_BI:
                    return (
                        <div className="fm_dashboard_forceBI_container">
                            <DashboardForceBI
                                entity={DASHBOARD}
                                forceBIId={dashboard.id}
                                title={dashboard.title}
                            />
                        </div>
                    );
                case DASHBOARD_TYPE.DEFAULT:
                    return <DailyDashboard />;
            }
        };

        return loadedDashboards.map((dashboard) => (
            <div
                key={dashboard.id}
                className={classnames('fm-dashboard-container__item', {
                    'fm-dashboard-container__item--visible': dashboard?.id === activeDashboard?.id,
                })}
            >
                {getDashboardComponent(dashboard)}
            </div>
        ));
    }, [activeDashboard?.id, loadedDashboards]);

    return (
        <div className="fm-dashboard-container">
            {dashboardsLoaded}
            {!!activeDashboard?.config?.background && (
                <CustomBackground
                    color={activeDashboard?.config?.background?.color}
                    brightness={activeDashboard?.config?.background?.brightness}
                />
            )}
        </div>
    );
});

export default DashboardContainer;
